/**
 * Wall segments (PRP-SKETCH-FIELD-4 K6, N6). Where another wall meets the middle of
 * a room's wall (a T), that wall is two segments at that point.
 *
 * Segments are DERIVED, never stored (Decision Defaults, item 2.3): they come from the
 * plan wall graph (`buildWallGraph`, FIELD-3 J15) after every change, so they move with
 * the meeting wall and disappear when it moves away. A user's break is a stored corner
 * and stays a corner; a derived split is never written as one.
 *
 * Coordinates: a room edge's segments are offsets along its interior face from its
 * start corner (`edgeStart`), the frame openings use. A junction is where the meeting
 * wall's CENTERLINE crosses this wall's centerline (the graph's T), projected onto the
 * face, so a segment's length is centerline to centerline at a T, as Xactimate stores it.
 *
 * Per-segment measurement (DD-SF4.9): Wall length on a segment whose end is a T sets the
 * segment by moving the meeting wall (`moveHeldSide`: the meeting room's line, and any
 * room sharing it, J17). The reading is kept as a `spans` record on this room tying this
 * room's corner (or the other T's corner) to the meeting room's corner (corner ids are
 * plan-unique; `spans` are device-only and never exported). A segment is measured when
 * such a record ties its two ends. When a T moves away, a record whose two ends are no
 * longer a segment's ends is dropped (`pruneSegmentSpans`) and the caller names it
 * (DD default: kept only if both of its corners survive as the segment's ends).
 */
import { MANUAL_SOURCE, POLICY, SAME_TOL_FT, SHARED_TOL_FT } from './policy';
import { EPS, area, axisOf, dist, edgeOf, findRoom, outN, tidy, unit } from './geom';
import { buildUF, edgeKnown } from './uf';
import { sharedMap } from './shared';
import { wallFtOf } from './thickness';
import { buildWallGraph, type WallGraph } from './graph';
import { moveHeldSide } from './heldMove';
import type { Axis, Corner, Fail, Measurement, Plan, Pt, Room } from './types';

/** The wall that makes a T: the meeting room, its wall (start corner id) and its corner at the T. */
export interface MeetingWall { rid: string; vid: number; cid: number }

/** One end of a segment: this room's corner, or a T. */
export type SegmentEnd =
  | { kind: 'corner'; id: number } // policy-literal-ok: segment end vocabulary
  | { kind: 'junction'; at: Pt; meet: MeetingWall | null };

export interface EdgeSegment {
  index: number;
  /** Offsets along the edge's interior face from its start corner, feet. */
  s0: number;
  s1: number;
  lengthFt: number;
  ends: [SegmentEnd, SegmentEnd];
  /** Solid: a whole wall that is known, or a T segment tied by a recorded reading (DD-SF4.9). */
  known: boolean;
}

/** An edge's T junctions: offsets along its face, strictly inside it, sorted. */
export interface EdgeJunction { s: number; at: Pt; meet: MeetingWall | null }

const MIN_FT = () => POLICY.min_segment_ft;

/** The wall graph, built once per call chain. */
export const graphOf = (plan: Plan, G?: WallGraph): WallGraph => G ?? buildWallGraph(plan);

/** The other room's wall whose centerline ends at junction `p` on edge (a, b) of `r`. */
function meetingWall(plan: Plan, r: Room, a: Corner, b: Corner, p: Pt): MeetingWall | null {
  const t = axisOf(a, b);
  const along: Axis = t === 'h' ? 'x' : 'y', norm: Axis = t === 'h' ? 'y' : 'x';
  const tE = wallFtOf(r, a.id);
  let best: { m: MeetingWall; d: number } | null = null;
  for (const q of plan.rooms) {
    const A = area(q), n = q.corners.length;
    for (let i = 0; i < n; i++) {
      const c = q.corners[i], d = q.corners[(i + 1) % n];
      if (axisOf(c, d) !== (t === 'h' ? 'v' : 'h')) continue;
      if (q === r && (c.id === a.id || c.id === b.id || d.id === a.id || d.id === b.id)) continue;
      const tF = wallFtOf(q, c.id), nO = outN(q, c, d, A);
      const centre = c[along] + nO[along] * (tF / 2);
      if (Math.abs(centre - p[along]) > SHARED_TOL_FT) continue;
      for (const end of [c, d]) {
        const gap = Math.abs(end[norm] - p[norm]);
        if (gap > tE + tF + SHARED_TOL_FT) continue;
        if (!best || gap < best.d) best = { m: { rid: q.id, vid: c.id, cid: end.id }, d: gap };
      }
    }
  }
  return best?.m ?? null;
}

/**
 * Where edge `i`'s centerline run starts and ends, as offsets along its face from its start
 * corner: where it meets the centerlines of the room's walls either side (graph.ts
 * `roomRuns`). Beyond the face at a convex corner, short of it at a reflex one.
 */
function centerlineRun(r: Room, i: number, u: Pt, n: Pt, half: number, L: number): [number, number] {
  const m = r.corners.length, A = area(r), a = r.corners[i];
  const q = { x: a.x + n.x * half, y: a.y + n.y * half };
  const live = (k: number, step: 1 | -1): [Corner, Corner] | null => {
    for (let j = 1; j < m; j++) {
      const s0 = r.corners[(((k + step * j) % m) + m) % m], s1 = r.corners[(((k + step * j + 1) % m) + m) % m];
      if (dist(s0, s1) > EPS) return [s0, s1];
    }
    return null;
  };
  const at = (F: [Corner, Corner] | null, dflt: number): number => {
    if (!F) return dflt;
    const nF = outN(r, F[0], F[1], A), den = u.x * nF.x + u.y * nF.y;
    if (Math.abs(den) < EPS) return dflt;
    const tF = wallFtOf(r, F[0].id);
    return (tF / 2 - ((q.x - F[0].x) * nF.x + (q.y - F[0].y) * nF.y)) / den;
  };
  return [at(live(i, -1), 0), at(live(i, 1), L)];
}

/** The T junctions on wall `vid` of room `rid` (K6), from the wall graph. */
export function edgeJunctions(plan: Plan, rid: string, vid: number, G?: WallGraph): EdgeJunction[] {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return [];
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return [];
  const L = dist(E.a, E.b), u = unit(E.a, E.b), n = outN(r, E.a, E.b);
  const half = wallFtOf(r, vid) / 2;
  const [run0, run1] = centerlineRun(r, E.i, u, n, half, L);
  const out: EdgeJunction[] = [];
  for (const p of graphOf(plan, G).junctions) {
    const off = (p.x - E.a.x) * n.x + (p.y - E.a.y) * n.y;
    if (Math.abs(off - half) > SHARED_TOL_FT) continue;
    const s = (p.x - E.a.x) * u.x + (p.y - E.a.y) * u.y;
    if (s <= MIN_FT() || s >= L - MIN_FT()) continue;
    // Strictly inside this wall's own centerline run (graph.ts segmentsOf's rule): at a
    // reflex corner the room's own next wall ends half a wall in from the face corner,
    // which is this wall's end, not a T.
    if (s <= run0 + SHARED_TOL_FT || s >= run1 - SHARED_TOL_FT) continue;
    if (out.some((j) => Math.abs(j.s - s) <= SHARED_TOL_FT)) continue;
    const meet = meetingWall(plan, r, E.a, E.b, p);
    // A T within half the meeting wall of this wall's own corner is that corner (the meeting
    // wall's face lines up with the corner; its centerline lands half a wall short of it).
    const mHalf = meet ? wallFtOf(findRoom(plan, meet.rid)!, meet.vid) / 2 : 0;
    if (s <= mHalf + SHARED_TOL_FT || s >= L - mHalf - SHARED_TOL_FT) continue;
    out.push({ s, at: { x: p.x, y: p.y }, meet });
  }
  return out.sort((x, y) => x.s - y.s);
}

/** The segments of wall `vid` of room `rid` (K6): one per stretch between corners and T's. */
export function edgeSegments(plan: Plan, rid: string, vid: number, G?: WallGraph): EdgeSegment[] {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return [];
  const L = dist(E.a, E.b);
  const J = edgeJunctions(plan, rid, vid, G);
  const ends: SegmentEnd[] = [
    { kind: 'corner', id: E.a.id }, // policy-literal-ok: vocabulary
    ...J.map((j): SegmentEnd => ({ kind: 'junction', at: j.at, meet: j.meet })),
    { kind: 'corner', id: E.b.id }, // policy-literal-ok: vocabulary
  ];
  const cuts = [0, ...J.map((j) => j.s), L];
  const wholeKnown = edgeKnown(buildUF(r), E.a, E.b);
  return cuts.slice(0, -1).map((s0, k) => {
    const s1 = cuts[k + 1];
    const e: [SegmentEnd, SegmentEnd] = [ends[k], ends[k + 1]];
    const known = J.length ? !!segmentSpan(r, e) : wholeKnown;
    return { index: k, s0, s1, lengthFt: s1 - s0, ends: e, known };
  });
}

/** The segment at offset `off` along the edge (the nearer one at a T). */
export function segmentAt(segs: EdgeSegment[], off: number): EdgeSegment | null {
  if (!segs.length) return null;
  return segs.find((g) => off >= g.s0 - EPS && off <= g.s1 + EPS) ?? (off < segs[0].s0 ? segs[0] : segs[segs.length - 1]);
}

/** The segment an opening is on: the one its middle is in. */
export function openingSegment(plan: Plan, rid: string, oid: number, G?: WallGraph): EdgeSegment | null {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o) return null;
  return segmentAt(edgeSegments(plan, rid, o.edgeStart, G), o.offFt + o.widthFt / 2);
}

/** The corner id standing for a segment end (a T stands for its meeting room's corner). */
const endId = (e: SegmentEnd): number | null => (e.kind === 'corner' ? e.id : e.meet?.cid ?? null); // policy-literal-ok: vocabulary

/** The recorded reading that ties a T segment's two ends (DD-SF4.9), if any. */
function segmentSpan(r: Room, e: [SegmentEnd, SegmentEnd]): Measurement | undefined {
  const a = endId(e[0]), b = endId(e[1]);
  if (a == null || b == null) return undefined;
  return r.spans?.find((m) => isSegmentSpan(m) && ((m.a === a && m.b === b) || (m.a === b && m.b === a)));
}

/**
 * A segment-length record (DD-SF4.9): a `len` span carrying a `source`. Older spans (FIELD-2's
 * split-line records) never had one, so they are never taken for a segment reading.
 */
const isSegmentSpan = (m: Measurement): boolean => m.kind === 'len' && m.source != null;

export type SegmentLengthResult =
  | { ok: true; moved: string[] }
  | Fail<'missing' | 'not-a-segment' | 'too-short' | 'no-meeting' | 'measured' | 'too-small' | 'same'>;

/**
 * DD-SF4.9: set T segment `index` of wall `vid` to `valueFt` by moving the meeting wall at
 * its far T end (its near end when only the start is a T), then record the reading. A
 * segment with no T end is the whole wall: use Wall length. One undo step.
 */
export function setSegmentLength(plan: Plan, rid: string, vid: number, index: number, valueFt: number): SegmentLengthResult {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const segs = edgeSegments(plan, rid, vid);
  const g = segs[index];
  if (!g || segs.length < 2) return { ok: false, err: 'not-a-segment', message: 'This wall has no other wall meeting it. Use Wall length.' };
  if (valueFt < POLICY.length_min_ft) return { ok: false, err: 'too-short', message: 'Too short. Check the reading.' };
  // The T that moves: the far end, unless only the near end is a T.
  const farT = g.ends[1].kind === 'junction';
  const end = farT ? g.ends[1] : g.ends[0];
  if (end.kind !== 'junction' || !end.meet) {
    return { ok: false, err: 'no-meeting', message: 'The wall that meets here is not a room wall the app can move. Move it by hand.' };
  }
  const delta = valueFt - g.lengthFt;
  const t = axisOf(E.a, E.b);
  const along: Axis = t === 'h' ? 'x' : 'y';
  const u = unit(E.a, E.b);
  const shift = (farT ? 1 : -1) * delta * u[along];
  if (Math.abs(delta) >= SAME_TOL_FT) {
    const q = findRoom(plan, end.meet.rid)!;
    const res = moveHeldSide(plan, q, buildUF(q), along, end.meet.vid, shift, sharedMap(plan));
    if (!res.ok) return res;
  }
  const a = endId(g.ends[0]), b = endId(g.ends[1]);
  if (a == null || b == null) return { ok: false, err: 'no-meeting', message: 'The wall that meets here is not a room wall the app can move. Move it by hand.' };
  r.spans = (r.spans ?? []).filter((m) => !(m.kind === 'len' && ((m.a === a && m.b === b) || (m.a === b && m.b === a))));
  r.spans.push({ axis: along, a, b, valueFt, kind: 'len', source: MANUAL_SOURCE });
  tidy(plan);
  return { ok: true, moved: Math.abs(delta) >= SAME_TOL_FT ? [end.meet.rid] : [] };
}

/**
 * After a change: drop every segment reading whose two ends are no longer the ends of a
 * segment of its room (the T moved away or merged, DD default). Returns what went, for
 * the toast.
 */
export function pruneSegmentSpans(plan: Plan): { rid: string; valueFt: number }[] {
  const dropped: { rid: string; valueFt: number }[] = [];
  const G = buildWallGraph(plan);
  for (const r of plan.rooms) {
    if (!r.spans?.length) continue;
    const pairs = new Set<string>();
    r.corners.forEach((c) => edgeSegments(plan, r.id, c.id, G).forEach((g) => {
      const a = endId(g.ends[0]), b = endId(g.ends[1]);
      if (a != null && b != null && g.ends.some((e) => e.kind === 'junction')) pairs.add(`${Math.min(a, b)}:${Math.max(a, b)}`);
    }));
    const kept = r.spans.filter((m) => {
      if (!isSegmentSpan(m)) return true;
      const ok = pairs.has(`${Math.min(m.a, m.b)}:${Math.max(m.a, m.b)}`);
      if (!ok) dropped.push({ rid: r.id, valueFt: m.valueFt });
      return ok;
    });
    if (kept.length) r.spans = kept; else delete r.spans;
  }
  return dropped;
}
